/* ==========================================================================
   CLYDE CLONE — cb-product.js
   Product page: gallery, variant matching, add-ons, sticky bar.
   Deliberately standalone — it does not touch Dawn's own variant-selects,
   so the two systems cannot fight over the same form.
   ========================================================================== */

(function () {
  'use strict';

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '£{{amount}}').replace('{{amount}}', parts.join('.'));
  }

  function initPdp(root) {
    if (!root || root.dataset.cbReady === '1') return;
    root.dataset.cbReady = '1';

    // Targeted by class: {% form %} rejects hyphenated parameters, so the
    // form itself cannot carry a data attribute.
    var form = root.querySelector('form.cb-pdp__form');
    var cartUrl = root.dataset.cbCartUrl || '/cart';
    var addUrl = root.dataset.cbAddUrl || '/cart/add';
    var idInput = root.querySelector('[data-cb-variant-id]');
    var priceEl = root.querySelector('[data-cb-price]');
    var compareEl = root.querySelector('[data-cb-compare]');
    var atcBtn = root.querySelector('[data-cb-atc]');
    var atcText = root.querySelector('[data-cb-atc-text]');
    var errorEl = root.querySelector('[data-cb-error]');
    var dynamic = root.querySelector('[data-cb-dynamic]');
    var dynamicNote = root.querySelector('[data-cb-dynamic-note]');
    var stickyPrice = root.querySelector('[data-cb-sticky-price]');

    var variants = [];
    var raw = root.querySelector('[data-cb-variants]');
    if (raw) {
      try { variants = JSON.parse(raw.textContent); } catch (e) { variants = []; }
    }

    var moneyFormat = (window.Shopify && window.Shopify.money_format) || '£{{amount}}';

    /* ---------- Gallery ---------- */
    var thumbs = [].slice.call(root.querySelectorAll('[data-cb-thumb]'));
    var frames = [].slice.call(root.querySelectorAll('[data-cb-frame]'));

    function showFrame(index) {
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.dataset.cbThumb === String(index));
      });
      frames.forEach(function (f) {
        var on = f.dataset.cbFrame === String(index);
        f.classList.toggle('is-active', on);
        // A video left playing in a hidden frame keeps its audio running.
        if (!on) {
          var v = f.querySelector('video');
          if (v && !v.paused) v.pause();
        }
      });
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showFrame(t.dataset.cbThumb); });
    });

    /* --- Touch swipe on the main image (mobile) --- */
    var swipeStage = root.querySelector('.cb-pdp__stage, .cb-fp__stage');
    if (swipeStage && frames.length > 1) {
      var sx = 0, sy = 0, tracking = false;

      function activeIndex() {
        for (var i = 0; i < frames.length; i++) {
          if (frames[i].classList.contains('is-active')) return i;
        }
        return 0;
      }

      swipeStage.addEventListener('touchstart', function (e) {
        // Leave 3D models alone — they use swipe to rotate.
        if (e.touches.length !== 1 || e.target.closest('model-viewer')) { tracking = false; return; }
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });

      swipeStage.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;

        var next = activeIndex() + (dx < 0 ? 1 : -1);
        if (next < 0) next = frames.length - 1;
        if (next >= frames.length) next = 0;
        showFrame(next);

        var frame = frames[next];
        frame.classList.remove('cb-swipe-in-left', 'cb-swipe-in-right');
        void frame.offsetWidth;
        frame.classList.add(dx < 0 ? 'cb-swipe-in-right' : 'cb-swipe-in-left');

        var th = thumbs[next];
        if (th && th.parentNode && th.parentNode.scrollTo) {
          var p = th.parentNode;
          p.scrollTo({ left: th.offsetLeft - (p.clientWidth - th.clientWidth) / 2, behavior: 'smooth' });
        }
      }, { passive: true });
    }

    /* --- Thumb strip arrows --- */
    var strip = root.querySelector('[data-cb-thumbs]');
    var tPrev = root.querySelector('[data-cb-thumb-prev]');
    var tNext = root.querySelector('[data-cb-thumb-next]');

    if (strip && tPrev && tNext) {
      function thumbStep() {
        var one = strip.querySelector('.cb-pdp__thumb');
        if (!one) return strip.clientWidth;
        var cs = window.getComputedStyle(strip);
        var gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
        return (one.getBoundingClientRect().width + gap) * 3;
      }

      function syncThumbNav() {
        // Only worth showing when there is somewhere to scroll to.
        var overflows = strip.scrollWidth > strip.clientWidth + 4;
        tPrev.hidden = !overflows || strip.scrollLeft <= 2;
        tNext.hidden = !overflows ||
          strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 2;
      }

      tPrev.addEventListener('click', function () {
        strip.scrollBy({ left: -thumbStep(), behavior: 'smooth' });
      });
      tNext.addEventListener('click', function () {
        strip.scrollBy({ left: thumbStep(), behavior: 'smooth' });
      });

      strip.addEventListener('scroll', syncThumbNav, { passive: true });
      window.addEventListener('resize', syncThumbNav);
      // Images may still be loading, so measure again once they settle.
      window.setTimeout(syncThumbNav, 300);
      syncThumbNav();
    }

    function showMedia(mediaId) {
      if (!mediaId) return;
      var frame = root.querySelector('[data-cb-media-id="' + mediaId + '"][data-cb-frame]');
      if (frame) showFrame(frame.dataset.cbFrame);
    }

    /* ---------- Image zoom (Amazon-style hover magnifier) ----------
       Rewritten to depend on as little as possible: it reads the zoom
       source straight off whichever <img> is already on screen (proven to
       work, since the gallery/thumbnails already render it) instead of a
       separately-rendered data attribute, and it builds the lens/pane
       elements itself in JS instead of trusting markup to be present. The
       only thing this needs from the page is the .cb-pdp__stage wrapper. */
    try {
      // Matches both the real product page (.cb-pdp__stage) and the
      // homepage Featured Product tile (.cb-fp__stage) — same JS engine
      // boots both via [data-cb-pdp].
      var zoomStage = root.querySelector('.cb-pdp__stage, .cb-fp__stage');
      if (zoomStage && window.matchMedia) {
        (function () {
          var stage = zoomStage;
          var zoomFactor = 2.6;
          var active = false;

          // Guard against any stray/duplicate lens or pane elements already
          // sitting in the DOM (leftover markup, a second script run, etc.)
          // so the ones this closure controls below are the only ones that
          // exist — nothing else can be shadowing them.
          [].slice.call(document.querySelectorAll('.cb-pdp__zoom-lens, .cb-pdp__zoom-pane')).forEach(function (el) {
            if (el.parentNode) el.parentNode.removeChild(el);
          });

          var lens = document.createElement('div');
          lens.className = 'cb-pdp__zoom-lens';
          lens.setAttribute('aria-hidden', 'true');
          lens.hidden = true;
          stage.appendChild(lens);

          var pane = document.createElement('div');
          pane.className = 'cb-pdp__zoom-pane';
          pane.setAttribute('aria-hidden', 'true');
          pane.hidden = true;
          pane.style.position = 'fixed';
          document.body.appendChild(pane);

          var mql = window.matchMedia(
            '(hover: hover) and (pointer: fine) and (min-width: 990px)'
          );

          function bigSrc(img) {
            // Shopify's CDN URLs carry a `width=` query param; bumping it
            // gives the pane a crisper crop than the on-page copy. If the
            // pattern isn't there for some reason, the on-page image still
            // works fine as the zoom source — just less sharp enlarged.
            var src = img.currentSrc || img.src || '';
            if (/[?&]width=\d+/.test(src)) {
              return src.replace(/([?&])width=\d+/, '$1width=2200');
            }
            return src;
          }

          function currentImg() {
            // [data-cb-frame] matches both .cb-pdp__frame and .cb-fp__frame
            // — the attribute is shared across both gallery markups.
            var frame = stage.querySelector('[data-cb-frame].is-active');
            var img = frame && frame.querySelector('img');
            if (!img || !img.src) return null;
            return img;
          }

          function positionPane() {
            var r = stage.getBoundingClientRect();
            var vw = window.innerWidth;
            var vh = window.innerHeight;
            // pane.offsetWidth is only reliable once it's unhidden (show()
            // runs before this), so it reflects the real CSS-computed width.
            var paneW = pane.offsetWidth || Math.min(736, vw * 0.38);

            var left = r.right + 24;
            // Not enough room to the right of the image (narrow window,
            // dev tools docked, a tight info column) — flip to the left
            // of the image instead of letting the pane run off-screen.
            if (left + paneW > vw - 8) {
              left = r.left - 24 - paneW;
            }
            // Still doesn't fit either side — clamp inside the viewport
            // rather than clip off an edge.
            left = Math.max(8, Math.min(left, vw - paneW - 8));
            var top = Math.max(8, Math.min(r.top, vh - r.height - 8));

            pane.style.top = top + 'px';
            pane.style.left = left + 'px';
            pane.style.height = r.height + 'px';
          }

          function show() {
            active = true;
            pane.hidden = false;
            lens.hidden = false;
            pane.setAttribute('aria-hidden', 'false');
            lens.setAttribute('aria-hidden', 'false');
            // Bulletproof: force visibility via an inline !important style,
            // which always wins over any external stylesheet rule (even one
            // also marked !important) regardless of whatever is fighting the
            // [hidden] attribute/CSS cascade elsewhere on the page.
            pane.style.setProperty('display', 'block', 'important');
            lens.style.setProperty('display', 'block', 'important');
          }

          function hide() {
            active = false;
            pane.hidden = true;
            lens.hidden = true;
            pane.setAttribute('aria-hidden', 'true');
            lens.setAttribute('aria-hidden', 'true');
            pane.style.setProperty('display', 'none', 'important');
            lens.style.setProperty('display', 'none', 'important');
          }

          function move(e) {
            var img = currentImg();
            if (!img) { hide(); return; }

            var rect = img.getBoundingClientRect();
            var x = e.clientX - rect.left;
            var y = e.clientY - rect.top;

            if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
              hide();
              return;
            }

            if (!active) show();
            positionPane();
            pane.style.backgroundImage = 'url(' + bigSrc(img) + ')';

            var lensW = rect.width / zoomFactor;
            var lensH = rect.height / zoomFactor;
            var lensX = Math.min(Math.max(x - lensW / 2, 0), rect.width - lensW);
            var lensY = Math.min(Math.max(y - lensH / 2, 0), rect.height - lensH);

            lens.style.width = lensW + 'px';
            lens.style.height = lensH + 'px';
            lens.style.transform = 'translate(' + lensX + 'px, ' + lensY + 'px)';

            var pctX = lensX / (rect.width - lensW || 1);
            var pctY = lensY / (rect.height - lensH || 1);
            var paneRect = pane.getBoundingClientRect();
            var bgW = rect.width * zoomFactor;
            var bgH = rect.height * zoomFactor;

            pane.style.backgroundSize = bgW + 'px ' + bgH + 'px';
            pane.style.backgroundPosition =
              (-(pctX * (bgW - paneRect.width))) + 'px ' +
              (-(pctY * (bgH - paneRect.height))) + 'px';
          }

          function wire() {
            if (!mql.matches) { hide(); return; }
            stage.addEventListener('mousemove', move);
            stage.addEventListener('mouseleave', hide);
          }

          function unwire() {
            stage.removeEventListener('mousemove', move);
            stage.removeEventListener('mouseleave', hide);
            hide();
          }

          wire();
          var onQueryChange = function () { unwire(); wire(); };
          if (mql.addEventListener) {
            mql.addEventListener('change', onQueryChange);
          } else if (mql.addListener) {
            mql.addListener(onQueryChange);
          }
        })();
      }
    } catch (zoomErr) {
      // Never let the zoom feature take the rest of the product page down
      // with it — the gallery/variant/cart code below must still run.
      if (window.console && console.error) console.error('cb zoom:', zoomErr);
    }

    /* ---------- Variant matching ---------- */
    function selectedOptions() {
      var groups = [].slice.call(root.querySelectorAll('[data-cb-option-group]'));
      return groups.map(function (g) {
        var sel = g.querySelector('select[data-cb-option-input]');
        if (sel) return sel.value;
        var checked = g.querySelector('[data-cb-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(opts) {
      return variants.find(function (v) {
        return opts.every(function (o, i) { return v.options[i] === o; });
      });
    }

    function applyVariant(v) {
      if (!v) {
        if (atcBtn) atcBtn.disabled = true;
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }

      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.textContent = money(v.price, moneyFormat);
      if (stickyPrice) stickyPrice.textContent = money(v.price, moneyFormat);

      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = money(v.compare_at_price, moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (atcBtn) atcBtn.disabled = !v.available;
      if (atcText) atcText.textContent = v.available ? 'Add To Cart' : 'Sold Out';

      if (v.featured_media && v.featured_media.id) showMedia(v.featured_media.id);

      // Keep the address bar in step so a copied link reopens this variant.
      if (window.history && window.history.replaceState && root.dataset.cbUrl) {
        window.history.replaceState({}, '', root.dataset.cbUrl + '?variant=' + v.id);
      }
    }

    root.querySelectorAll('[data-cb-option-group]').forEach(function (group) {
      group.addEventListener('change', function () {
        var checked = group.querySelector('[data-cb-option-input]:checked');
        var readout = group.querySelector('[data-cb-option-readout]');
        // data-cb-label holds the display form; .value stays the raw option
        // value because variant matching depends on it.
        if (checked && readout) {
          readout.textContent = checked.dataset.cbLabel || checked.value;
        }
        applyVariant(findVariant(selectedOptions()));
      });
    });

    /* ---------- Quantity ---------- */
    var qtyInput = root.querySelector('.cb-qty__input');
    root.querySelectorAll('[data-cb-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var next = parseInt(qtyInput.value, 10) + parseInt(btn.dataset.cbQty, 10);
        qtyInput.value = Math.max(1, next);
      });
    });

    /* ---------- Add-ons ----------
       Express checkout skips the cart entirely, so any extras the shopper
       ticked would be silently dropped. Hide it while extras are selected. */
    var addonBoxes = [].slice.call(root.querySelectorAll('[data-cb-addon]'));

    function addonVariantId(box) {
      var select = root.querySelector('[data-cb-addon-variants="' + box.id + '"]');
      return select ? select.value : box.dataset.cbAddonId;
    }

    function selectedAddons() {
      return addonBoxes.filter(function (b) { return b.checked; })
        .map(function (b) { return { id: addonVariantId(b), quantity: 1 }; });
    }

    function syncDynamic() {
      var any = selectedAddons().length > 0;
      if (dynamic) dynamic.hidden = any;
      if (dynamicNote) dynamicNote.hidden = !any;
    }

    addonBoxes.forEach(function (b) { b.addEventListener('change', syncDynamic); });
    root.querySelectorAll('[data-cb-addon-variants]').forEach(function (sel) {
      sel.addEventListener('change', syncDynamic);
    });
    syncDynamic();

    /* ---------- Submit ---------- */
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function addToCart() {
      if (!form || !idInput) return;

      // Required line-item choices are plain radios; let the browser report them.
      if (!form.reportValidity()) return;

      if (errorEl) errorEl.hidden = true;
      if (atcBtn) atcBtn.disabled = true;
      if (atcText) atcText.textContent = 'Adding…';

      var properties = {};
      form.querySelectorAll('input[name^="properties["]:checked').forEach(function (input) {
        var key = input.name.replace('properties[', '').replace(']', '');
        properties[key] = input.value;
      });
      // Dropdown choices (e.g. Color) — skip the empty "Choose an Option".
      form.querySelectorAll('select[name^="properties["]').forEach(function (sel) {
        if (!sel.value) return;
        var key = sel.name.replace('properties[', '').replace(']', '');
        properties[key] = sel.value;
      });

      var items = [{
        id: idInput.value,
        quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1,
        properties: properties
      }].concat(selectedAddons());

      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.data.description || res.data.message || 'Could not add to cart.');
          window.location = cartUrl;
        })
        .catch(function (err) {
          showError(err.message);
          if (atcBtn) atcBtn.disabled = false;
          if (atcText) atcText.textContent = 'Add To Cart';
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        addToCart();
      });
    }

    var stickyAdd = root.querySelector('[data-cb-sticky-add]');
    if (stickyAdd) stickyAdd.addEventListener('click', addToCart);

    /* ---------- Sticky bar ---------- */
    var sticky = root.querySelector('[data-cb-sticky]');
    if (sticky && atcBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        sticky.hidden = entries[0].isIntersecting;
      }, { rootMargin: '0px 0px -40px 0px' }).observe(atcBtn);
    }

    /* ---------- Upsell carousel ---------- */
    root.querySelectorAll('[data-cb-upsell]').forEach(function (car) {
      var track = car.querySelector('[data-cb-upsell-track]');
      var prev = car.querySelector('[data-cb-upsell-prev]');
      var next = car.querySelector('[data-cb-upsell-next]');
      var dots = [].slice.call(car.querySelectorAll('[data-cb-upsell-dot]'));
      if (!track) return;

      function cardWidth() {
        var card = track.querySelector('.cb-addon__card');
        if (!card) return track.clientWidth;
        var cs = window.getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function currentIndex() {
        var w = cardWidth();
        return w ? Math.round(track.scrollLeft / w) : 0;
      }

      function sync() {
        var i = currentIndex();
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
      });
      dots.forEach(function (d, n) {
        d.addEventListener('click', function () {
          track.scrollTo({ left: cardWidth() * n, behavior: 'smooth' });
        });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });

    /* ---------- Dismissible banner ---------- */
    root.querySelectorAll('[data-cb-dismiss]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var box = btn.closest('[data-cb-dismissible]');
        if (box) box.hidden = true;
      });
    });

    // Sync once on load in case the URL already carries ?variant=
    applyVariant(findVariant(selectedOptions()));
  }

  function boot() {
    document.querySelectorAll('[data-cb-pdp]').forEach(initPdp);
  }

  boot();
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-cb-pdp]') : null;
    if (el) { el.dataset.cbReady = ''; initPdp(el); }
  });
})();
